import { useState, useMemo } from "react";
import {
    Button,
    Card,
    CardBody,
    Chip,
    Input,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Spinner,
    Select,
    SelectItem,
} from "@heroui/react";
import {
    Search,
    Lock,
    Edit,
    Shield,
    Filter,
    RefreshCw,
} from "lucide-react";
import { usePermissions, useUpdatePermission } from "@src/presentation/hooks/rbac";
import { ToastService } from "@src/shared/services/toast.service";
import type { Permission } from "@src/domain/rbac/entities/permission.entity";

export default function PermissionsPage() {
    const [searchQuery, setSearchQuery] = useState("");
    const [resourceFilter, setResourceFilter] = useState<string>("all");
    const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
    const [description, setDescription] = useState("");

    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

    // Fetch data
    const { data: permissions = [], isLoading, refetch } = usePermissions();

    // Mutations
    const updatePermissionMutation = useUpdatePermission();

    // Get unique resources for filtering
    const resources = useMemo(() => {
        return Array.from(new Set(permissions.map((p) => p.resource))).sort();
    }, [permissions]);

    // Filter permissions
    const filteredPermissions = useMemo(() => {
        return permissions.filter((permission) => {
            const matchesSearch =
                permission.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                permission.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                permission.resource.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesResource = resourceFilter === "all" || permission.resource === resourceFilter;

            return matchesSearch && matchesResource;
        });
    }, [permissions, searchQuery, resourceFilter]);

    // Stats
    const stats = useMemo(() => ({
        total: permissions.length,
        resources: resources.length,
        filtered: filteredPermissions.length,
    }), [permissions, resources, filteredPermissions]);

    // Open edit modal
    const handleEdit = (permission: Permission) => {
        setSelectedPermission(permission);
        setDescription(permission.description);
        onOpen();
    };

    // Save permission
    const handleSave = async () => {
        if (!selectedPermission) return;

        if (!description.trim()) {
            ToastService.error("Description is required");
            return;
        }

        try {
            await updatePermissionMutation.mutateAsync({
                permissionId: selectedPermission.permissionId,
                data: { description },
            });
            ToastService.success("Permission updated successfully");
            onClose();
        } catch (error: any) {
            ToastService.error(error.message || "Failed to update permission");
        }
    };

    // Group permissions by resource
    const groupedPermissions = useMemo(() => {
        return filteredPermissions.reduce((acc, permission) => {
            const resource = permission.resource;
            if (!acc[resource]) {
                acc[resource] = [];
            }
            acc[resource].push(permission);
            return acc;
        }, {} as Record<string, Permission[]>);
    }, [filteredPermissions]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-96">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded bg-primary">
                                <Lock className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">Permissions Management</h1>
                            {/* <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                                {filteredPermissions.length} {filteredPermissions.length === 1 ? "permission" : "permissions"}
                            </span> */}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Button
                                color="default"
                                variant="flat"
                                onPress={() => refetch()}
                                isLoading={isLoading}
                                startContent={<RefreshCw className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Refresh</span>
                                <span className="sm:hidden">Refresh</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Stats Overview */}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <Card className="border border-border rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                                        <Lock className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-default-500">Total Permissions</p>
                                        <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border border-success/20 bg-success/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-success/10">
                                        <Shield className="h-5 w-5 text-success" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-success">Resources</p>
                                        <p className="text-2xl font-bold text-success">{stats.resources}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border border-warning/20 bg-warning/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-warning/10">
                                        <Filter className="h-5 w-5 text-warning" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-warning">Filtered</p>
                                        <p className="text-2xl font-bold text-warning">{stats.filtered}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Filters */}
                    <Card className="border border-border rounded shadow-none">
                        <CardBody className="p-4">
                            <div className="flex flex-wrap gap-3">
                                <Input
                                    placeholder="Search permissions..."
                                    value={searchQuery}
                                    onValueChange={setSearchQuery}
                                    startContent={<Search className="h-4 w-4 text-default-400" />}
                                    className="max-w-xs"
                                    classNames={{
                                        inputWrapper: "rounded border border-border",
                                    }}
                                />
                                <Select
                                    placeholder="Filter by resource"
                                    selectedKeys={new Set([resourceFilter])}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0] as string;
                                        setResourceFilter(selected || "all");
                                    }}
                                    className="max-w-xs"
                                    startContent={<Filter className="h-4 w-4" />}
                                    items={[{ key: "all", label: "All Resources" }, ...resources.map(r => ({ key: r, label: r }))]}
                                    classNames={{
                                        trigger: "rounded",
                                        popoverContent: "rounded",
                                    }}
                                >
                                    {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
                                </Select>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Permissions by Resource */}
                    <div className="space-y-6">
                        {Object.entries(groupedPermissions).map(([resource, perms]) => (
                            <Card key={resource} className="border border-border rounded shadow-none">
                                <CardBody className="p-0">
                                    {/* Resource Header */}
                                    <div className="flex items-center gap-3 border-b border-border p-4 bg-default-50">
                                        <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                                            <Shield className="h-4 w-4 text-primary" />
                                        </div>
                                        <h3 className="text-base font-semibold uppercase text-primary">
                                            {resource}
                                        </h3>
                                        <Chip size="sm" variant="flat" color="primary" className="rounded">
                                            {perms.length}
                                        </Chip>
                                    </div>

                                    {/* Permissions Table */}
                                    <div className="overflow-x-auto">
                                        <Table
                                            aria-label={`${resource} permissions`}
                                            removeWrapper
                                            classNames={{
                                                th: "bg-default-50 text-default-600 font-semibold",
                                                td: "py-4",
                                            }}
                                        >
                                            <TableHeader>
                                                <TableColumn>PERMISSION NAME</TableColumn>
                                                <TableColumn className="hidden md:table-cell">ACTION</TableColumn>
                                                <TableColumn className="hidden md:table-cell">DESCRIPTION</TableColumn>
                                                <TableColumn>ACTIONS</TableColumn>
                                            </TableHeader>
                                            <TableBody>
                                                {perms.map((permission) => (
                                                    <TableRow key={permission.permissionId} className="hover:bg-default-50 min-h-[64px]">
                                                        <TableCell>
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex h-8 w-8 items-center justify-center rounded bg-default/10">
                                                                    <Lock className="h-4 w-4 text-default-500" />
                                                                </div>
                                                                <span className="font-mono text-sm font-medium">
                                                                    {permission.name}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="hidden md:table-cell">
                                                            <Chip
                                                                size="sm"
                                                                variant="flat"
                                                                color="secondary"
                                                                className="rounded"
                                                            >
                                                                {permission.action}
                                                            </Chip>
                                                        </TableCell>
                                                        <TableCell className="hidden md:table-cell">
                                                            <span className="text-sm text-default-500">
                                                                {permission.description}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                isIconOnly
                                                                size="sm"
                                                                variant="light"
                                                                onPress={() => handleEdit(permission)}
                                                                className="rounded"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>

                    {Object.keys(groupedPermissions).length === 0 && (
                        <Card className="border border-border rounded shadow-none">
                            <CardBody className="p-12 text-center">
                                <Lock className="h-12 w-12 mx-auto text-default-300 mb-4" />
                                <p className="text-lg font-medium text-foreground">No permissions found</p>
                                <p className="text-sm text-default-500 mt-1">
                                    Try adjusting your search or filters
                                </p>
                            </CardBody>
                        </Card>
                    )}
                </div>
            </main>

            {/* Edit Modal */}
            <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg" classNames={{ base: "rounded" }}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="border-b border-border">Edit Permission</ModalHeader>
                            <ModalBody className="py-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-default-700 mb-2 block">
                                            Permission Name
                                        </label>
                                        <div className="p-3 bg-default-100 rounded border border-border">
                                            <p className="font-mono text-sm font-medium text-foreground">
                                                {selectedPermission?.name}
                                            </p>
                                        </div>
                                        <p className="text-xs text-default-500 mt-1">
                                            Permission name and resource cannot be changed
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-default-700 mb-2 block">
                                            Resource
                                        </label>
                                        <div className="p-3 bg-default-100 rounded border border-border">
                                            <p className="text-sm text-foreground">{selectedPermission?.resource}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-default-700 mb-2 block">
                                            Action
                                        </label>
                                        <div className="p-3 bg-default-100 rounded border border-border">
                                            <p className="text-sm text-foreground">{selectedPermission?.action}</p>
                                        </div>
                                    </div>
                                    <Input
                                        label="Description"
                                        placeholder="Enter permission description"
                                        value={description}
                                        onValueChange={setDescription}
                                        classNames={{
                                            inputWrapper: "rounded border border-border",
                                        }}
                                    />
                                </div>
                            </ModalBody>
                            <ModalFooter className="border-t border-border">
                                <Button variant="flat" onPress={onClose} className="rounded">
                                    Cancel
                                </Button>
                                <Button
                                    color="primary"
                                    onPress={handleSave}
                                    isLoading={updatePermissionMutation.isPending}
                                    className="rounded"
                                >
                                    Update
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
}
